import historyModule from '../../history';
import url from '../../url';

/**
 * "Interface" for a search- and event tracker. Don't use it directly, but get a working implementation by invoking
 * AnalyticsTracker.getInstance() instead.
 *
 * @param {Window} window The window object that is used to access globals of tracking providers.
 * @param {Array} [window.dataLayer] Possible Google Tag Manager queue.
 * @param {function} [window.ga] Possible Google Analytics function.
 * @param {Array} [window._gaq] Possible deprecated Google Analytics queue.
 * @param {{}} config Findologic JS config.
 * @param {{}} config.tracking
 * @param {{}} config.tracking.analytics
 * @param {string|undefined} config.tracking.analytics.customTracker Value for overriding tracker auto-detection.
 * @constructor
 */
export default function AnalyticsTracker(window, config) {
    this.window = window;
    this.config = config;

    // noinspection JSUnusedLocalSymbols (arguments used in descending prototypes)
    /**
     * Tracks a search request.
     *
     * @param {string} query The user's query as a URL query string.
     * @param {string} filter The last selected filter as a URL query string.
     * @param {function} [callback] Called once the request has been tracked, tracking failed, or timed out.
     *      It can be assumed that this function is called always. No arguments are passed.
     */
    /* eslint-disable no-unused-vars */
    this.trackSearch = function (query, filter, callback) {
        throw new Error('Method must be implemented by a specific Analytics tracking implementation.');
    };

    // noinspection JSUnusedLocalSymbols (arguments used in descending prototypes)
    /**
     * Tracks a generic event.
     *
     * @param {string} event The type of event, usually something like "findologicEvent".
     * @param {string} category Thee event category, e.g. "FINDOLOGIC Click".
     * @param {string} action The user interaction that caused this event, e.g. "Search-Concept".
     * @param {string} label The specific thing the user interacted with.
     * @param {function} [callback] Called once the request has been tracked, tracking failed, or timed out.
     *      It can be assumed that this function is called always. No arguments are passed.
     */
    this.trackEvent = function (event, category, action, label, callback) {
        throw new Error('Method must be implemented by a specific Analytics tracking implementation.');
    };

    /**
     * Parse tracking parameters from URL and track the event
     *
     * @param {string} trackingUrl The URL including the tracking parameters
     */
    this.trackParameters = function (trackingUrl) {
        const trackingParameterKeys = ['flTrackingCategory', 'flTrackingAction', 'flTrackingLabel'];
        const urlWithoutParams = trackingUrl.split('?')[0];

        const queryString = url.getQueryString(trackingUrl);
        if (!queryString) {
            return;
        }

        const queryParameters = url.parseQueryString(queryString);
        if (!queryParameters) {
            return;
        }

        const shopParameters = {};
        const trackingParameters = {};
        for (const parameterKey in queryParameters) {
            if (trackingParameterKeys.includes(parameterKey) && typeof queryParameters[parameterKey] === 'string') {
                trackingParameters[parameterKey] = queryParameters[parameterKey];
            } else {
                shopParameters[parameterKey] = queryParameters[parameterKey];
            }
        }

        if (Object.keys(trackingParameters).length === trackingParameterKeys.length) {
            this.trackEvent(
                'findologicEvent',
                trackingParameters.flTrackingCategory,
                trackingParameters.flTrackingAction,
                trackingParameters.flTrackingLabel
            );
        }

        if (Object.keys(trackingParameters).length > 0) {
            historyModule.replaceState(
                history.state,
                document.title,
                urlWithoutParams + (Object.keys(shopParameters).length ? '?' + url.stringifyQueryParams(shopParameters) : '')
            );
        }
    };
    /* eslint-enable no-unused-vars */
}

/**
 * @type {string} Path used for tracking page views.
 */
AnalyticsTracker.SEARCH_PATH = '/findologic/search?';

/**
 * @type {number} Time in milliseconds before considering a slow tracking request lost. This is the latest point in
 *      time at which the callback can be invoked.
 */
AnalyticsTracker.TIMEOUT = 1000;
